/**
 * ScamBlocker AI — Dashboard Overview (/)
 * Gerçek KPI grid + 7 günlük çağrı trendi (/api/org/portal/dashboard + reports/weekly).
 */

import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Mic, Phone, Shield, Users } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '@/shared/lib/api/client';
import { API } from '@/shared/lib/api/endpoints';
import styles from './Overview.module.css';

interface DashboardKpi {
  calls_7d: { total: number; active: number; high_risk: number };
  staff_count: number;
  recordings_count: number;
  storage_enabled: boolean;
}

interface WeeklyDay {
  day: string;
  calls: number;
  avg_risk: number | null;
  critical: number;
}

function Kpi({
  icon,
  value,
  label,
  tone,
}: {
  icon: ReactNode;
  value: number | string;
  label: string;
  tone: string;
}) {
  return (
    <div className={styles.kpiCard}>
      <div className={styles.kpiIcon} style={{ color: tone, background: `${tone}1a` }}>
        {icon}
      </div>
      <div className={styles.kpiContent}>
        <span className={styles.kpiNumber}>{value}</span>
        <span className={styles.kpiLabel}>{label}</span>
      </div>
    </div>
  );
}

export function Overview() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuery<DashboardKpi>({
    queryKey: ['dashboard-kpi'],
    queryFn: async () => {
      const { data } = await apiClient.get(API.org.dashboard);
      return data;
    },
  });

  // Haftalık trend — can_export izni gerektirir; yoksa sessizce gizlenir.
  const { data: weekly } = useQuery<{ daily: WeeklyDay[] }>({
    queryKey: ['dashboard-weekly'],
    queryFn: async () => {
      const { data } = await apiClient.get(API.org.reportWeekly);
      return data;
    },
    retry: false,
  });

  const days = weekly?.daily ?? [];
  const maxCalls = Math.max(1, ...days.map((d) => d.calls));

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t('overview.title')}</h1>
        <p className={styles.subtitle}>{t('overview.subtitle')}</p>
      </div>

      {isLoading && (
        <div className={styles.center}>
          <span className={styles.spinner} />
        </div>
      )}

      {isError && <div className={styles.errorBox}>{t('overview.loadError')}</div>}

      {data && (
        <>
          <div className={styles.kpiGrid}>
            <Kpi
              icon={<Phone size={20} />}
              value={data.calls_7d.total}
              label="Toplam Çağrı (7g)"
              tone="var(--color-primary)"
            />
            <Kpi
              icon={<Shield size={20} />}
              value={data.calls_7d.active}
              label="Aktif Çağrı"
              tone="var(--color-success)"
            />
            <Kpi
              icon={<AlertTriangle size={20} />}
              value={data.calls_7d.high_risk}
              label="Yüksek Risk"
              tone="var(--color-danger)"
            />
            <Kpi
              icon={<Users size={20} />}
              value={data.staff_count}
              label="Personel"
              tone="var(--color-info, var(--color-primary))"
            />
            <Kpi
              icon={<Mic size={20} />}
              value={data.recordings_count}
              label="Kayıt"
              tone="var(--color-warning)"
            />
          </div>

          {days.length > 0 && (
            <section className={styles.chartCard}>
              <h3 className={styles.chartTitle}>Günlük Çağrı Hacmi (7 gün)</h3>
              <div className={styles.chart}>
                {days.map((d) => {
                  const h = Math.round((d.calls / maxCalls) * 100);
                  const hot = d.critical > 0;
                  return (
                    <div
                      key={d.day}
                      className={styles.barCol}
                      title={`${d.calls} çağrı · ${d.critical} kritik`}
                    >
                      <div className={styles.barValue}>{d.calls}</div>
                      <div
                        className={styles.bar}
                        style={{
                          height: `${Math.max(4, h)}%`,
                          background: hot ? 'var(--color-danger)' : 'var(--color-primary)',
                        }}
                      />
                      <div className={styles.barLabel}>
                        {new Date(d.day).toLocaleDateString('tr-TR', {
                          day: '2-digit',
                          month: '2-digit',
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
